import { FC, useCallback, useEffect, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { IAdminProgramCodeFormValues } from '@interfaces/adminProgramCodes';
import { handleAdminEditProgramCode } from '@store/features/adminProgramCodes/actions';
import { removeAdminProgramCodeDetails } from '@store/features/adminProgramCodes/slice';
import useDispatch from '@store/hooks/useDispatch';
import { EventType, useEventBus } from '@utils/eventBus';
import handleToast from '@utils/handleToast';

import ProgramCodeDetailsForm from '../ProgramCodeDetailsForm/ProgramCodeDetailsForm';

import { IProgramCodeDetailsProps } from './ProgramCodeDetails.interface';
import {
  convertAdminProgramCodeDetailsIntoFormValues,
  convertFormValuesToEditPayload,
  validateForm,
} from './utils';

const ProgramCodeDetails: FC<IProgramCodeDetailsProps> = ({ data }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const eventBus = useEventBus('adminProgramCodeDetails');

  const initialValues = useMemo(() => convertAdminProgramCodeDetailsIntoFormValues(data), [data]);

  const handleSubmit = useCallback(
    (values: IAdminProgramCodeFormValues) => {
      const payload = convertFormValuesToEditPayload(data.id, values);
      return dispatch(handleAdminEditProgramCode(payload));
    },
    [dispatch, data],
  );

  const handleCancel = useCallback(() => {
    dispatch(removeAdminProgramCodeDetails());
  }, [dispatch]);

  useEffect(() => {
    eventBus.on(EventType.ADMIN_EDIT_PROGRAM_CODE_SUCCESS, () =>
      handleToast('success', t('admin.editProgramCodeSuccess')),
    );

    return () => eventBus.off(EventType.ADMIN_EDIT_PROGRAM_CODE_SUCCESS);
  }, [t, eventBus]);

  return (
    <Form
      // @ts-ignore because of custom props
      component={ProgramCodeDetailsForm}
      initialValues={initialValues}
      validate={validateForm}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );
};

export default ProgramCodeDetails;
